import React from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  LineChart, Line, Area, AreaChart, CartesianGrid
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useLanguage } from '../context/LanguageContext';
import { formatCurrency } from '../lib/utils';

// Category colors matching the app theme
const CATEGORY_COLORS = {
  logement: '#FF4F00',
  transport: '#3B82F6',
  alimentation: '#10B981',
  abonnements: '#8B5CF6',
  shopping: '#EC4899',
  loisirs: '#F59E0B',
  sante: '#EF4444',
  epargne: '#00D68F',
  education: '#6366F1',
  animaux: '#A855F7',
  enfants: '#F472B6',
  cadeaux: '#FB923C',
  entretien: '#78716C',
  professionnel: '#0EA5E9',
  divers: '#A8A29E',
};

const CATEGORY_NAMES_FR = {
  logement: 'Logement',
  transport: 'Transport',
  alimentation: 'Alimentation',
  abonnements: 'Abonnements',
  shopping: 'Shopping',
  loisirs: 'Loisirs',
  sante: 'Santé',
  epargne: 'Épargne',
  education: 'Éducation',
  animaux: 'Animaux',
  enfants: 'Enfants',
  cadeaux: 'Cadeaux',
  entretien: 'Entretien',
  professionnel: 'Professionnel',
  divers: 'Divers',
};

// Custom tooltip for pie chart
const CustomPieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-border">
        <p className="font-medium">{data.name}</p>
        <p className="text-primary font-bold">{formatCurrency(data.value)}</p>
        <p className="text-sm text-muted-foreground">{data.percentage}%</p>
      </div>
    );
  }
  return null;
};

// Custom tooltip for bar/line charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-border">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Pie Chart for spending by category
export function CategoryPieChart({ data, title }) {
  const { language, t } = useLanguage();
  
  const chartData = Object.entries(data || {}).map(([category, info]) => ({
    name: t(`cat_${category}`) || category,
    value: info.total || info,
    percentage: info.percentage || Math.round((info.total || info) / Object.values(data).reduce((sum, v) => sum + (v.total || v), 0) * 100),
    color: CATEGORY_COLORS[category] || '#A8A29E'
  })).filter(item => item.value > 0).sort((a, b) => b.value - a.value);

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent className="h-64 flex items-center justify-center">
          <p className="text-muted-foreground">
            {t('no_data')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Legend */}
        <div className="flex flex-wrap gap-2 mt-4 justify-center">
          {chartData.slice(0, 6).map((entry, index) => (
            <div key={index} className="flex items-center gap-1 text-xs">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-muted-foreground">{entry.name}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Bar Chart for monthly comparison
export function MonthlyBarChart({ data, title }) {
  const { language, t } = useLanguage();
  
  const monthNames = language === 'fr'
    ? ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
    : language === 'pt'
    ? ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const chartData = Object.entries(data || {}).map(([month, info]) => ({
    month: monthNames[parseInt(month) - 1],
    dépenses: info.total || 0,
    transactions: info.count || 0
  })).filter(item => item.dépenses > 0);

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent className="h-64 flex items-center justify-center">
          <p className="text-muted-foreground">
            {t('no_data')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}€`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="dépenses" 
                name={t('expenses')}
                fill="#FF4F00" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Area Chart for spending trend
export function SpendingTrendChart({ data, title }) {
  const { language, t } = useLanguage();
  
  const monthNames = language === 'fr'
    ? ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
    : language === 'pt'
    ? ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const chartData = Object.entries(data || {}).map(([month, info]) => ({
    month: monthNames[parseInt(month) - 1],
    montant: info.total || 0
  }));

  // Calculate cumulative for trend
  let cumulative = 0;
  const trendData = chartData.map(item => {
    cumulative += item.montant;
    return { ...item, cumul: cumulative };
  });

  if (trendData.length === 0 || trendData.every(d => d.montant === 0)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent className="h-64 flex items-center justify-center">
          <p className="text-muted-foreground">
            {t('no_data')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trendData}>
              <defs>
                <linearGradient id="colorMontant" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF4F00" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#FF4F00" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}€`} />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="montant" 
                name={t('expenses')}
                stroke="#FF4F00" 
                fillOpacity={1} 
                fill="url(#colorMontant)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Budget Progress Chart
export function BudgetProgressChart({ budgets, title }) {
  const { language, t } = useLanguage();
  
  const chartData = (budgets || []).map(budget => ({
    category: t(`cat_${budget.category}`) || budget.category,
    budget: budget.amount,
    spent: budget.spent || 0,
    remaining: Math.max(0, budget.amount - (budget.spent || 0)),
    percentage: budget.percentage || 0,
    color: CATEGORY_COLORS[budget.category] || '#A8A29E'
  })).filter(b => b.budget > 0);

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent className="h-64 flex items-center justify-center">
          <p className="text-muted-foreground">
            {language === 'fr' ? 'Aucun budget défini' : language === 'pt' ? 'Nenhum orçamento definido' : 'No budgets defined'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}€`} />
              <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={80} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="spent" 
                name={t('spent')}
                stackId="a" 
                fill="#FF4F00" 
              />
              <Bar 
                dataKey="remaining" 
                name={language === 'fr' ? 'Restant' : language === 'pt' ? 'Restante' : 'Remaining'}
                stackId="a" 
                fill="#E5E7EB" 
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Mini sparkline for dashboard
export function MiniSparkline({ data, color = '#FF4F00' }) {
  const chartData = (data || []).map((value, index) => ({ value, index }));
  
  if (chartData.length < 2) return null;

  return (
    <div className="h-12 w-24">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke={color} 
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
